import React from 'react';
import type { LucideIcon } from 'lucide-react';
import CompanyLogo from '@/components/common/CompanyLogo';
import { COMPANY_NAME, COMPANY_TAGLINE } from '@/lib/brand';
import {
  BarChart3,
  Building2,
  Calculator,
  ClipboardList,
  FileSpreadsheet,
  LineChart,
  PieChart,
  Scale,
  Shield,
  Table,
  TrendingUp,
} from 'lucide-react';

type FloatIcon = {
  Icon: LucideIcon;
  className: string;
  size: number;
  delay?: string;
};

/**
 * تزئین حاشیه — روی موبایل مخفی (تداخل با کارت).
 * از md فقط گوشه‌ها؛ از xl حاشیهٔ چپ/راست دور از کارت مرکزی.
 */
const FLOAT_ICONS: FloatIcon[] = [
  {
    Icon: Calculator,
    className: 'top-5 right-5 lg:top-8 lg:right-12',
    size: 26,
  },
  {
    Icon: BarChart3,
    className: 'top-5 left-5 lg:top-8 lg:left-12',
    size: 24,
    delay: '0.5s',
  },
  {
    Icon: PieChart,
    className: 'bottom-36 left-5 lg:bottom-40 lg:left-12',
    size: 24,
    delay: '0.9s',
  },
  {
    Icon: Shield,
    className: 'bottom-36 right-5 lg:bottom-40 lg:right-12',
    size: 24,
    delay: '1.2s',
  },
  {
    Icon: FileSpreadsheet,
    className: 'hidden xl:block top-[22%] right-8 2xl:right-14',
    size: 28,
    delay: '0.3s',
  },
  {
    Icon: TrendingUp,
    className: 'hidden xl:block top-[22%] left-8 2xl:left-14',
    size: 28,
    delay: '1.4s',
  },
  {
    Icon: Table,
    className: 'hidden xl:block top-[48%] right-6 2xl:right-12',
    size: 26,
    delay: '0.7s',
  },
  {
    Icon: LineChart,
    className: 'hidden xl:block top-[48%] left-6 2xl:left-12',
    size: 26,
    delay: '1.6s',
  },
  {
    Icon: Scale,
    className: 'hidden 2xl:block bottom-[46%] right-16',
    size: 28,
    delay: '1s',
  },
  {
    Icon: ClipboardList,
    className: 'hidden 2xl:block bottom-[46%] left-16',
    size: 26,
    delay: '0.4s',
  },
];

export const LoginIconFloats: React.FC = () => (
  <div
    className="pointer-events-none absolute inset-0 z-[2] hidden overflow-hidden md:block"
    aria-hidden
  >
    {FLOAT_ICONS.map(({ Icon, className, size, delay }, i) => (
      <span
        key={i}
        className={`absolute select-none text-emerald-800/16 ${
          i % 2 === 0 ? 'animate-math-float' : 'animate-math-drift'
        } ${className}`}
        style={delay ? { animationDelay: delay } : undefined}
      >
        <Icon size={size} strokeWidth={1.35} />
      </span>
    ))}

    <span
      className="absolute bottom-44 left-16 hidden select-none font-mono text-[10px] tracking-[0.2em] text-emerald-900/12 2xl:block animate-pulse-soft"
      dir="ltr"
    >
      BOQ
    </span>
    <span
      className="absolute bottom-44 right-16 hidden select-none font-mono text-[10px] tracking-[0.2em] text-emerald-900/12 2xl:block animate-pulse-soft"
      dir="ltr"
      style={{ animationDelay: '1s' }}
    >
      CPA
    </span>
  </div>
);

export const LoginBrandFooter: React.FC = () => (
  <footer
    className="pointer-events-none absolute bottom-0 left-0 right-0 z-[6] flex flex-col items-center justify-end px-3 pb-4 pt-2 sm:pb-6"
    role="contentinfo"
  >
    <div
      className="mb-2 hidden items-center justify-center gap-6 text-emerald-800/35 lg:flex"
      aria-hidden
    >
      <Calculator className="h-5 w-5 shrink-0" strokeWidth={1.4} />
      <Building2 className="h-5 w-5 shrink-0" strokeWidth={1.4} />
      <BarChart3 className="h-5 w-5 shrink-0" strokeWidth={1.4} />
      <PieChart className="h-5 w-5 shrink-0" strokeWidth={1.4} />
      <Table className="h-5 w-5 shrink-0" strokeWidth={1.4} />
      <Shield className="h-5 w-5 shrink-0" strokeWidth={1.4} />
    </div>
    <CompanyLogo
      className="mb-1.5 flex justify-center sm:mb-2"
      imgClassName="h-9 w-auto max-w-[9.5rem] object-contain sm:h-11 sm:max-w-[12rem]"
    />
    <p className="text-center text-[0.75rem] font-semibold tracking-tight text-emerald-950/75 sm:text-sm">
      {COMPANY_NAME}
    </p>
    <p className="mt-0.5 max-w-[18rem] text-center text-[0.65rem] leading-snug text-emerald-900/50 sm:max-w-none sm:text-xs">
      {COMPANY_TAGLINE}
    </p>
  </footer>
);
